import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { 
  FiHome, 
  FiUsers, 
  FiCalendar, 
  FiCheckSquare,
  FiSettings,
  FiUser,
  FiLogOut,
  FiMenu,
  FiX,
  FiBell,
  FiSearch,
  FiChevronRight,
  FiChevronDown,
  FiUserCheck,
  FiUserPlus,
  FiShield,
  FiPlus,
  FiBarChart2,
  FiBriefcase,
  FiMapPin,
  FiHeart,
  FiBookOpen,
  FiGift,
  FiMoreHorizontal
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import './Layout.css';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, canReadModule } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [expandedOnMobile, setExpandedOnMobile] = useState(null);

  const perfilCriancas = user?.permissao === 'reuniao_criancas';

  // Handle resize with useCallback
  const handleResize = useCallback(() => {
    const mobile = window.innerWidth < 1024;
    setIsMobile(mobile);
    if (!mobile) {
      setSidebarOpen(true);
      setShowMoreMenu(false);
    } else {
      setSidebarOpen(false);
    }
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setShowMoreMenu(false);
    }
  }, [location, isMobile]);

  // Handle window resize
  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Theme toggle
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setDarkMode(true);
      document.documentElement.setAttribute('data-theme', 'dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !darkMode;
    setDarkMode(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', newTheme ? 'dark' : 'light');
  };

  const handleLogout = () => {
    logout();
    toast.success('Logout realizado com sucesso!');
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Bottom navigation items (mobile) - filtrados por permissão
  const bottomNavCandidates = [
    { path: '/dashboard',   icon: FiHome,        text: 'Início',      module: 'dashboard'   },
    { path: '/irmandade',   icon: FiUsers,       text: 'Irmandade',   module: 'irmandade'   },
    { path: '/recitativos', icon: FiBookOpen,    text: 'Recitativos', module: 'recitativos' },
    { path: '/reunioes',    icon: FiCalendar,    text: 'Reuniões',    module: 'reunioes'    },
    { path: '/presencas',   icon: FiCheckSquare, text: 'Presenças',   module: 'presencas'   },
  ];
  const bottomNavItems = perfilCriancas
    ? [
        { path: '/dashboard',                              icon: FiHome,        text: 'Início'       },
        { path: '/irmandade',                              icon: FiUsers,       text: 'Irmandade'    },
        { path: '/reuniao-criancas/aniversariantes',       icon: FiGift,        text: 'Aniversários' },
        { path: '/reuniao-criancas/visitas-agendadas',     icon: FiCheckSquare, text: 'Visitas'      },
      ]
    : bottomNavCandidates
        .filter(it => !it.module || canReadModule(it.module))
        .slice(0, 4);

  // Navigation items with sections
  const navigationSections = [
    {
      title: 'Principal',
      items: [
        {
          path: '/dashboard',
          icon: FiHome,
          text: 'Dashboard',
          description: 'Visão geral do sistema',
          module: 'dashboard'
        }
      ]
    },
    {
      title: 'Gestão',
      items: [
        {
          path: '/cargos',
          icon: FiBriefcase,
          text: 'Cargos',
          description: 'Gerenciar cargos e funções',
          module: 'cargos',
          submenu: [
            { path: '/cargos', icon: FiBarChart2, text: 'Listar Cargos' },
            { path: '/cargos/novo', icon: FiPlus, text: 'Novo Cargo' }
          ]
        },
        {
          path: '/congregacoes',
          icon: FiHome,
          text: 'Congregações',
          description: 'Gerenciar congregações',
          module: 'congregacoes',
          submenu: [
            { path: '/congregacoes', icon: FiHome, text: 'Listar Congregações' }
          ]
        },
        {
          path: '/colaboradores',
          icon: FiUsers,
          text: 'Colaboradores',
          description: 'Gerenciar colaboradores',
          module: 'colaboradores',
          submenu: [
            { path: '/colaboradores', icon: FiUsers, text: 'Listar Colaboradores' },
            { path: '/colaboradores/novo', icon: FiUserPlus, text: 'Novo Colaborador' }
          ]
        },
        {
          path: '/reunioes',
          icon: FiCalendar,
          text: 'Reuniões',
          description: 'Gerenciar reuniões',
          module: 'reunioes',
          submenu: [
            { path: '/reunioes', icon: FiCalendar, text: 'Listar Reuniões' },
            { path: '/reunioes/nova', icon: FiPlus, text: 'Nova Reunião' }
          ]
        },
        {
          path: '/irmandade',
          icon: FiUsers,
          text: 'Irmandade',
          description: 'Gerenciar membros da irmandade',
          module: 'irmandade',
          submenu: [
            { path: '/irmandade', icon: FiUsers, text: 'Membros' },
            { path: '/irmandade/cadastro', icon: FiPlus, text: 'Novo Membro' }
          ]
        },
        {
          path: '/recitativos',
          icon: FiBookOpen,
          text: 'Recitativos',
          description: 'Emitir recitativos e marcar frequência',
          module: 'recitativos',
          submenu: [
            { path: '/recitativos', icon: FiBookOpen, text: 'Lista de Recitativos' },
            { path: '/recitativos/novo', icon: FiPlus, text: 'Novo Recitativo' }
          ]
        },
        {
          path: '/reuniao-jovens',
          icon: FiBarChart2,
          text: 'Reunião de Jovens',
          description: 'Comparecimento, recitativos e analítico por congregação',
          module: 'reuniao_jovens',
          submenu: [
            { path: '/reuniao-jovens', icon: FiCalendar, text: 'Reuniões' },
            { path: '/reuniao-jovens/dashboard', icon: FiBarChart2, text: 'Dashboard' },
            { path: '/reuniao-jovens/agenda', icon: FiCalendar, text: 'Agenda recorrente' }
          ]
        },
        {
          path: '/reuniao-criancas',
          icon: FiHeart,
          text: 'Reunião de Crianças',
          description: 'Presença, visitas de aniversário e dashboard',
          module: 'reuniao_criancas',
          submenu: [
            { path: '/reuniao-criancas', icon: FiCalendar, text: 'Reuniões' },
            { path: '/reuniao-criancas/dashboard', icon: FiBarChart2, text: 'Dashboard' },
            { path: '/reuniao-criancas/agenda', icon: FiCalendar, text: 'Agenda recorrente' },
            { path: '/reuniao-criancas/aniversariantes', icon: FiGift, text: 'Aniversariantes' },
            { path: '/reuniao-criancas/visitas-agendadas', icon: FiCheckSquare, text: 'Visitas agendadas' }
          ]
        },
        {
          path: '/visitas',
          icon: FiMapPin,
          text: 'Visitas',
          description: 'Gerenciar visitas e atividades',
          module: 'visitas',
          submenu: [
            { path: '/visitas/mocidade', icon: FiUsers, text: 'Visita - Mocidade' },
            { path: '/visitas/criancas', icon: FiUsers, text: 'Visita - Crianças' },
            { path: '/visitas/ajuntamento', icon: FiCalendar, text: 'Ajuntamento' },
            { path: '/visitas/reuniao-familiar', icon: FiHome, text: 'Reunião Familiar' },
            { path: '/visitas/reuniao-evangelizacao', icon: FiHeart, text: 'Reunião Evangelização' }
          ]
        },
        {
          path: '/presencas',
          icon: FiCheckSquare,
          text: 'Presenças',
          description: 'Controlar presenças',
          module: 'presencas',
          submenu: [
            { path: '/presencas', icon: FiCheckSquare, text: 'Controle de Presença' },
            { path: '/presencas/relatorio', icon: FiBarChart2, text: 'Relatórios' }
          ]
        },
        {
          path: '/mapa',
          icon: FiMapPin,
          text: 'Territorial',
          description: 'Mapas e análise de frequência por território',
          module: 'irmandade',
          noAuxiliar: true,
          submenu: [
            { path: '/mapa', icon: FiMapPin, text: 'Territorialização', module: 'irmandade' },
            { path: '/analise-territorial-frequencia', icon: FiBarChart2, text: 'Análise de Frequência', module: 'analise_territorial' },
          ],
        },
        {
          path: '/rota-jovens',
          icon: FiMapPin,
          text: 'Rota de Visitas',
          description: 'Rota otimizada para visitar jovens',
          module: 'rota_visitas',
        }
      ]
    },
    {
      title: 'Administração',
      items: [
        {
          path: '/usuarios',
          icon: FiUserCheck,
          text: 'Usuários',
          description: 'Gerenciar usuários do sistema',
          submenu: [
            { path: '/usuarios', icon: FiUserCheck, text: 'Listar Usuários' },
            { path: '/usuarios/novo', icon: FiUserPlus, text: 'Novo Usuário' },
            { path: '/perfis-usuario', icon: FiShield, text: 'Perfis e Permissões' }
          ],
          adminOnly: true
        },
        {
          path: '/perfil',
          icon: FiUser,
          text: 'Meu Perfil',
          description: 'Alterar meus dados e senha'
        },
        {
          path: '/configuracoes',
          icon: FiSettings,
          text: 'Configurações',
          description: 'Configurações do sistema',
          adminOnly: true
        }
      ]
    }
  ];

  // Get breadcrumb from current path
  const getBreadcrumb = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);

    if (pathSegments.length === 0 || pathSegments[0] === 'dashboard') {
      return [{ text: 'Dashboard', path: '/dashboard' }];
    }

    // Map path segments to readable names
    const pathMap = {
      'cargos': 'Cargos',
      'congregacoes': 'Congregações',
      'colaboradores': 'Colaboradores',
      'reunioes': 'Reuniões',
      'presencas': 'Presenças',
      'irmandade': 'Irmandade',
      'recitativos': 'Recitativos',
      'visitas': 'Visitas',
      'criancas': 'Crianças',
      'jovens': 'Jovens',
      'adultos': 'Adultos',
      'todos': 'Todos',
      'mocidade': 'Mocidade',
      'ajuntamento': 'Ajuntamento',
      'reuniao-familiar': 'Reunião Familiar',
      'reuniao-evangelizacao': 'Reunião Evangelização',
      'mapa': 'Territorialização',
      'analise-territorial-frequencia': 'Análise Territorial de Frequência',
      'rota-jovens': 'Rota de Visitas',
      'reuniao-criancas': 'Reunião de Crianças',
      'aniversariantes': 'Aniversariantes',
      'visitas-agendadas': 'Visitas Agendadas',
      'usuarios': 'Usuários',
      'perfis-usuario': 'Perfis e Permissões',
      'configuracoes': 'Configurações',
      'novo': 'Novo',
      'nova': 'Nova',
      'cadastro': 'Cadastro',
      'editar': 'Editar'
    };

    const breadcrumbs = [];
    pathSegments.forEach((segment, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      const text = pathMap[segment] || segment;
      breadcrumbs.push({ text, path });
    });

    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumb();

  // Get current page title for mobile header
  const getCurrentPageTitle = () => {
    const crumbs = getBreadcrumb();
    return crumbs.length > 0 ? crumbs[crumbs.length - 1].text : 'Reunião';
  };

  // Check if user has admin permissions
  const isAdmin = user?.permissao === 'admin';
  const isAuxiliar = user?.permissao === 'auxiliar';

  // Filter navigation items based on permissions
  const getFilteredNavigation = () => {
    return navigationSections.map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (item.adminOnly && !isAdmin) return false;
        if (item.noAuxiliar && isAuxiliar) return false;
        if (item.module && !canReadModule(item.module)) return false;
        // Hide group if none of its submenu children are accessible
        if (item.submenu) {
          const accessible = item.submenu.filter(sub => !sub.module || canReadModule(sub.module));
          if (accessible.length === 0) return false;
        }
        return true;
      })
    })).filter(section => section.items.length > 0);
  };

  const filteredNavigation = getFilteredNavigation();

  /** Submenu: lista unificada em /irmandade (exclui cadastro/edição) */
  const isSubmenuLinkActive = (subPath) => {
    const p = location.pathname;
    if (subPath === '/irmandade/cadastro') return p === '/irmandade/cadastro';
    if (subPath === '/irmandade') {
      return (
        p.startsWith('/irmandade') &&
        p !== '/irmandade/cadastro' &&
        !p.startsWith('/irmandade/editar')
      );
    }
    return p === subPath;
  };

  // Check if current path is active
  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.nome) return 'U';
    return user.nome.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  return (
    <div className="app-layout">
      {/* Mobile Overlay */}
      {(sidebarOpen || showMoreMenu) && isMobile && (
        <div 
          className="mobile-overlay active"
          onClick={() => {
            closeSidebar();
            setShowMoreMenu(false);
          }}
        />
      )}

      {/* Sidebar - Desktop & Mobile Drawer */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'collapsed'} ${isMobile ? 'mobile-sidebar' : ''}`}>
        {/* Sidebar Header */}
        <div className="sidebar-header">
          <Link to="/dashboard" className="logo-container" onClick={closeSidebar}>
            <div className="logo-icon">
              <FiCalendar />
            </div>
            <div className="logo-text">
              <div className="logo-title">Reunião</div>
              <div className="logo-subtitle">Sistema de Gestão</div>
            </div>
          </Link>
          {isMobile && (
            <button className="sidebar-close-btn" onClick={closeSidebar} aria-label="Fechar menu">
              <FiX />
            </button>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="nav-menu">
          {filteredNavigation.map((section, sectionIndex) => (
            <div key={sectionIndex} className="nav-section">
              <div className="nav-section-title">{section.title}</div>
              {section.items.map((item, itemIndex) => {
                const anyChildActive = item.submenu?.some(sub => isActive(sub.path));
                const itemIsActive = isActive(item.path) || !!anyChildActive;
                const submenuVisivel = item.submenu && (
                  itemIsActive ||
                  (isMobile && expandedOnMobile === item.path)
                );
                const submenuFiltrado = item.submenu
                  ? item.submenu.filter(sub => !sub.module || canReadModule(sub.module))
                  : null;
                return (
                  <div key={itemIndex} className="nav-item">
                    {/* No mobile, itens com submenu expandem sem navegar */}
                    {isMobile && item.submenu ? (
                      <button
                        className={`nav-link nav-link-collapsible ${itemIsActive ? 'active' : ''} ${expandedOnMobile === item.path ? 'nav-link-expanded' : ''}`}
                        onClick={() => setExpandedOnMobile(prev => prev === item.path ? null : item.path)}
                        aria-expanded={expandedOnMobile === item.path}
                        title={item.description}
                      >
                        <item.icon className="nav-icon" />
                        <span className="nav-text">{item.text}</span>
                        <FiChevronDown className={`nav-chevron ${expandedOnMobile === item.path ? 'nav-chevron-open' : ''}`} />
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className={`nav-link ${itemIsActive ? 'active' : ''}`}
                        title={item.description}
                        onClick={isMobile ? closeSidebar : undefined}
                      >
                        <item.icon className="nav-icon" />
                        <span className="nav-text">{item.text}</span>
                        {item.submenu && <FiChevronDown className={`nav-chevron ${itemIsActive ? 'nav-chevron-open' : ''}`} />}
                      </Link>
                    )}

                    {/* Submenu */}
                    {submenuVisivel && submenuFiltrado && (
                      <div className="nav-submenu">
                        {submenuFiltrado.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            className={`nav-link ${isSubmenuLinkActive(subItem.path) ? 'active' : ''}`}
                            onClick={isMobile ? closeSidebar : undefined}
                          >
                            <subItem.icon className="nav-icon" />
                            <span className="nav-text">{subItem.text}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">
              {getUserInitials()}
            </div>
            <div className="user-info">
              <div className="user-name">{user?.nome || 'Usuário'}</div>
              <div className="user-role">{user?.permissao || 'viewer'}</div>
            </div>
          </div>
          
          {/* Mobile Sidebar user actions */}
          {isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', width: '100%' }}>
              <Link
                to="/perfil"
                className="sidebar-logout-btn"
                onClick={closeSidebar}
                style={{ textDecoration: 'none', background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)' }}
              >
                <FiUser />
                <span>Meu Perfil</span>
              </Link>
              <button className="sidebar-logout-btn" onClick={handleLogout}>
                <FiLogOut />
                <span>Sair</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`main-content ${!sidebarOpen && !isMobile ? 'expanded' : ''} ${isMobile ? 'mobile-main' : ''}`}>
        {/* Mobile Header */}
        {isMobile && (
          <header className="mobile-header">
            <button 
              className="mobile-header-action"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <FiMenu />
            </button>
            
            <h1 className="mobile-header-title">{getCurrentPageTitle()}</h1>
            
            <button 
              className="mobile-header-action"
              onClick={() => navigate('/perfil')}
              aria-label="Meu Perfil"
            >
              <FiUser />
            </button>
          </header>
        )}

        {/* Desktop Top Header */}
        {!isMobile && (
          <header className="top-header">
            <div className="header-content">
              <div className="header-left">
                <button 
                  className="menu-toggle"
                  onClick={toggleSidebar}
                  aria-label="Toggle Menu"
                >
                  {sidebarOpen ? <FiX /> : <FiMenu />}
                </button>

                {/* Breadcrumb */}
                <nav className="breadcrumb" aria-label="Breadcrumb">
                  {breadcrumbs.map((crumb, index) => (
                    <div key={index} className="breadcrumb-item">
                      {index > 0 && <FiChevronRight className="breadcrumb-separator" />}
                      {index === breadcrumbs.length - 1 ? (
                        <span className="breadcrumb-current">{crumb.text}</span>
                      ) : (
                        <Link to={crumb.path} className="breadcrumb-link">
                          {crumb.text}
                        </Link>
                      )}
                    </div>
                  ))}
                </nav>
              </div>

              <div className="header-right">
                {/* Role Badge */}
                <span className={`role-badge ${isAdmin ? 'admin' : 'user'}`}>
                  {isAdmin ? 'Admin' : 'Usuário'}
                </span>

                <div className="header-actions">
                  {/* Search Button */}
                  <button className="header-button" title="Buscar">
                    <FiSearch />
                  </button>

                  {/* Notifications */}
                  <button className="header-button" title="Notificações">
                    <FiBell />
                    <span className="notification-badge">3</span>
                  </button>

                  {/* Theme Toggle */}
                  <button 
                    className={`theme-toggle ${darkMode ? 'dark' : ''}`}
                    onClick={toggleTheme}
                    title={`Mudar para tema ${darkMode ? 'claro' : 'escuro'}`}
                  >
                    <span className="sr-only">
                      {darkMode ? 'Ativar tema claro' : 'Ativar tema escuro'}
                    </span>
                  </button>

                  {/* User Avatar Desktop */}
                  <div
                    className="header-user"
                    onClick={() => navigate('/perfil')}
                    title="Meu Perfil"
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="header-user-avatar">
                      {getUserInitials()}
                    </div>
                    <div className="header-user-info">
                      <span className="header-user-name">{user?.nome || 'Usuário'}</span>
                    </div>
                  </div>

                  {/* Logout */}
                  <button 
                    className="header-button logout-button" 
                    onClick={handleLogout}
                    title="Sair do sistema"
                  >
                    <FiLogOut />
                  </button>
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Content Wrapper */}
        <div className={`content-wrapper ${isMobile ? 'mobile-content' : ''}`}>
          <div className="page-container">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <nav className="bottom-nav" role="navigation" aria-label="Navegação principal">
          {bottomNavItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              className={`bottom-nav-item ${isActive(item.path) ? 'active' : ''}`}
            >
              <item.icon />
              <span>{item.text}</span>
            </Link>
          ))}
          <button 
            className={`bottom-nav-item ${showMoreMenu ? 'active' : ''}`}
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            aria-label="Mais opções"
          >
            <FiMoreHorizontal />
            <span>Mais</span>
          </button>
        </nav>
      )}

      {/* Mobile More Menu */}
      {isMobile && showMoreMenu && (
        <div className="more-menu">
          <div className="more-menu-content">
            <div className="more-menu-header">
              <h3>Mais opções</h3>
              <button onClick={() => setShowMoreMenu(false)} aria-label="Fechar">
                <FiX />
              </button>
            </div>
            <div className="more-menu-grid">
              {canReadModule('recitativos') && (
                <Link to="/recitativos" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                  <FiBookOpen />
                  <span>Recitativos</span>
                </Link>
              )}
              {canReadModule('cargos') && (
                <Link to="/cargos" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                  <FiBriefcase />
                  <span>Cargos</span>
                </Link>
              )}
              {canReadModule('congregacoes') && (
                <Link to="/congregacoes" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                  <FiHome />
                  <span>Congregações</span>
                </Link>
              )}
              {canReadModule('colaboradores') && (
                <Link to="/colaboradores" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                  <FiUsers />
                  <span>Colaboradores</span>
                </Link>
              )}
              {canReadModule('visitas') && (
                <Link to="/visitas" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                  <FiMapPin />
                  <span>Visitas</span>
                </Link>
              )}
              {canReadModule('reuniao_criancas') && (
                <Link to="/reuniao-criancas" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                  <FiHeart />
                  <span>R. Crianças</span>
                </Link>
              )}
              {(canReadModule('irmandade') || canReadModule('analise_territorial')) && !isAuxiliar && (
                <>
                  {canReadModule('irmandade') && (
                    <Link to="/mapa" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                      <FiMapPin />
                      <span>Territorialização</span>
                    </Link>
                  )}
                  {canReadModule('analise_territorial') && (
                    <Link to="/analise-territorial-frequencia" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                      <FiBarChart2 />
                      <span>Análise Territorial</span>
                    </Link>
                  )}
                </>
              )}
              {canReadModule('rota_visitas') && (
                <Link to="/rota-jovens" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                  <FiMapPin />
                  <span>Rota Jovens</span>
                </Link>
              )}
              {isAdmin && (
                <Link to="/usuarios" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                  <FiUserCheck />
                  <span>Usuários</span>
                </Link>
              )}
              <Link to="/perfil" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                <FiUser />
                <span>Meu Perfil</span>
              </Link>
              {isAdmin && (
                <Link to="/configuracoes" className="more-menu-item" onClick={() => setShowMoreMenu(false)}>
                  <FiSettings />
                  <span>Configurações</span>
                </Link>
              )}
              <button 
                className="more-menu-item more-menu-theme" 
                onClick={() => { toggleTheme(); setShowMoreMenu(false); }}
              >
                <span className={`theme-icon ${darkMode ? 'dark' : ''}`}>🌓</span>
                <span>{darkMode ? 'Tema Claro' : 'Tema Escuro'}</span>
              </button>
              <button 
                className="more-menu-item more-menu-logout" 
                onClick={() => { handleLogout(); setShowMoreMenu(false); }}
              >
                <FiLogOut />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
